import { useEffect, useState, useRef } from 'react';
import { useRouter } from 'next/router';
import '../styles/globals.css';

// ═══════════════════════════════════════════════════════════════════════
// ANTI-HMR REFRESH GUARD
// ═══════════════════════════════════════════════════════════════════════
// This block runs at MODULE LOAD time, the moment _app.js's bundle is
// parsed, before React even mounts. Its job is to neutralize Next.js'
// dev-mode HMR client when the app is accidentally deployed in dev mode
// (which Digital Ocean has been doing). The dev HMR client opens a
// WebSocket to /_next/webpack-hmr; that fails to tunnel through DO/CF's
// edge proxy; the client retries forever and eventually triggers a full
// page reload to "recover" — that's the refreshing-every-few-minutes.
//
// Defending here, in the browser, means it works regardless of whether
// the deployment is configured correctly. We were burned once already
// trying to put this in _document.js — that broke SSR with Turbopack +
// Next 16 dev mode. Module-level here is the safe spot.
if (typeof window !== 'undefined') {
  try {
    var __host = window.location.hostname || '';
    var __isLocal = __host === 'localhost' || __host === '127.0.0.1' || __host === '0.0.0.0' || __host === '';
    if (!__isLocal) {
      // 1) Replace WebSocket so HMR-related connections return a CLOSED stub
      var __OrigWS = window.WebSocket;
      if (__OrigWS) {
        var __PatchedWS = function (url, protocols) {
          var u = String(url || '');
          if (u.indexOf('webpack-hmr') !== -1 || u.indexOf('/_next/') !== -1 || u.indexOf('hmr') !== -1) {
            try { console.warn('[anti-hmr] Blocking dev WebSocket on prod host:', u); } catch (e) {}
            return {
              url: u,
              readyState: 3, // CLOSED
              protocol: '', extensions: '', bufferedAmount: 0,
              binaryType: 'blob',
              onopen: null, onmessage: null, onerror: null, onclose: null,
              send: function () {},
              close: function () {},
              addEventListener: function () {},
              removeEventListener: function () {},
              dispatchEvent: function () { return false; },
            };
          }
          return new __OrigWS(url, protocols);
        };
        __PatchedWS.CONNECTING = 0;
        __PatchedWS.OPEN = 1;
        __PatchedWS.CLOSING = 2;
        __PatchedWS.CLOSED = 3;
        __PatchedWS.prototype = __OrigWS.prototype;
        try { window.WebSocket = __PatchedWS; } catch (e) {}
      }

      // 2) Block fetches to dev-only resources
      try {
        var __origFetch = window.fetch;
        window.fetch = function (input, init) {
          var u = typeof input === 'string' ? input : (input && input.url) || '';
          if (
            u.indexOf('/_next/static/development/') !== -1 ||
            u.indexOf('webpack-hmr') !== -1 ||
            u.indexOf('/__nextjs_original-stack-frame') !== -1
          ) {
            return Promise.reject(new Error('blocked-dev-resource'));
          }
          return __origFetch.apply(this, arguments);
        };
      } catch (e) {}

      // 3) Refuse location.reload() calls coming from HMR/turbopack/refresh
      try {
        var __origReload = window.location.reload.bind(window.location);
        Object.defineProperty(window.location, 'reload', {
          configurable: true,
          value: function () {
            var stack = '';
            try { stack = (new Error()).stack || ''; } catch (e) {}
            var fromHMR = /webpack|hmr|hot-reloader|reactRefresh|turbopack/i.test(stack);
            try {
              var key = 'mt_unload_log';
              var raw = localStorage.getItem(key);
              var arr = raw ? JSON.parse(raw) : [];
              arr.unshift({
                ts: new Date().toISOString(),
                reason: fromHMR ? 'BLOCKED-hmr-reload' : 'location.reload',
                stack: stack.split('\n').slice(0, 5).join(' | '),
              });
              localStorage.setItem(key, JSON.stringify(arr.slice(0, 20)));
            } catch (e) {}
            if (fromHMR) {
              try { console.warn('[anti-hmr] Blocked location.reload() from HMR client'); } catch (e) {}
              return;
            }
            return __origReload();
          },
        });
      } catch (e) {}
    }
  } catch (e) {
    try { console.error('[anti-hmr] guard install failed', e); } catch (_) {}
  }
}
// ═══════════════════════════════════════════════════════════════════════

const DEBUG_AUTH = true;
function log(...args) {
  if (!DEBUG_AUTH) return;
  // eslint-disable-next-line no-console
  console.log('[auth]', new Date().toISOString().slice(11, 23), ...args);
}

function pushUnloadLog(entry) {
  try {
    const key = 'mt_unload_log';
    const raw = localStorage.getItem(key);
    const arr = raw ? JSON.parse(raw) : [];
    arr.unshift({ ...entry, ts: new Date().toISOString() });
    localStorage.setItem(key, JSON.stringify(arr.slice(0, 20)));
  } catch (e) {}
}

function MyApp({ Component, pageProps }) {
  const router = useRouter();
  const [authChecked, setAuthChecked] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const checkingRef = useRef(false);

  useEffect(() => {
    if (router.pathname === '/login') {
      setAuthChecked(true);
      setAuthenticated(true);
      return;
    }

    if (checkingRef.current) return;
    checkingRef.current = true;

    log('checking auth...');
    fetch('/api/auth/check', { credentials: 'same-origin' })
      .then((res) => {
        log('auth check status=', res.status);
        if (res.ok) {
          setAuthenticated(true);
        } else {
          log('not authenticated → /login');
          pushUnloadLog({ reason: 'auth-401-redirect-to-login', from: router.pathname });
          router.replace('/login');
        }
      })
      .catch((err) => {
        log('auth check failed', err && err.message);
        pushUnloadLog({ reason: 'auth-check-network-error', error: err && err.message });
        router.replace('/login');
      })
      .finally(() => {
        setAuthChecked(true);
        checkingRef.current = false;
      });
  }, [router.pathname]);

  useEffect(() => {
    function onPageHide(e) {
      pushUnloadLog({
        reason: 'pagehide',
        persisted: e.persisted,
        path: location.pathname,
        visibility: document.visibilityState,
      });
    }
    function onBeforeUnload() {
      pushUnloadLog({ reason: 'beforeunload', path: location.pathname });
    }
    function onError(e) {
      pushUnloadLog({
        reason: 'window-error',
        msg: e.message,
        src: e.filename,
        line: e.lineno,
      });
    }
    function onRejection(e) {
      pushUnloadLog({
        reason: 'unhandledrejection',
        msg: e.reason && (e.reason.message || String(e.reason)),
      });
    }

    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);

    try {
      const raw = localStorage.getItem('mt_unload_log');
      if (raw) {
        const arr = JSON.parse(raw);
        if (arr.length > 0) log('last unload reason →', arr[0]);
      }
    } catch (e) {}

    return () => {
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  if (!authChecked || !authenticated) {
    return null;
  }

  return <Component {...pageProps} />;
}

export default MyApp;
